import { IconLayer } from '@deck.gl/layers';
import demoThreatIcon from '../assets/icons/vessels/icon-threat.png';
import demoAuvTrimaran from '../assets/icons/demo/demo-auv-trimaran.png';
import demoDefensiveSurface from '../assets/icons/demo/demo-defensive-surface.png';
import demoAuvRaster from '../assets/icons/demo/autonomous-underwater-vehicle.png';
import {
  resolveVesselMaritimeHeadingDeg,
  deckIconAngleFromMaritimeHeadingDeg,
  VESSEL_ICON_BOW_COMPASS_AT_ANGLE_ZERO
} from './vesselMapIconAngle';

const SPECS = {
  threat: { atlas: demoThreatIcon, width: 1280, height: 698 },
  friendly: { atlas: demoAuvTrimaran, width: 1024, height: 558 },
  defensive: { atlas: demoDefensiveSurface, width: 1024, height: 305 },
  auv: { atlas: demoAuvRaster, width: 1024, height: 252 }
};

/**
 * IconLayer uses a single size (pixels) and keeps texture aspect ratio. Wide hull sprites
 * (e.g. defensive USV) would otherwise span much more screen than taller assets. Scale height
 * so max(width, height) in screen space matches `targetMaxPx` for every vessel kind.
 */
function iconLayerSizePixelsUniform(targetMaxPx, spec) {
  const w = spec.width;
  const h = spec.height;
  const aspect = w / h;
  const maxScreenFromUnitHeight = Math.max(1, aspect);
  return targetMaxPx / maxScreenFromUnitHeight;
}

function iconMappingFor(spec) {
  const anchorY = spec.height / 2;
  return {
    icon: {
      x: 0,
      y: 0,
      width: spec.width,
      height: spec.height,
      mask: false,
      anchorY
    }
  };
}

/**
 * Deck.gl IconLayers: threat (icon-threat), trimaran, defensive USV, designated AUVs (autonomous-underwater-vehicle.png).
 * Full-color PNGs (mask: false); getColor left white so art is unchanged.
 */
export function buildDemoVesselIconLayers({
  idPrefix,
  threatData = [],
  defensiveData = [],
  auvData = [],
  friendlyData = [],
  getPosition,
  /** Row used to read heading (e.g. exercise heads use d.tooltipRow || d). */
  getHeadingRow = (d) => d,
  sizePixels = 16,
  /** @deprecated Kept for API compat; sizing uses sizePixels for every kind. */
  threatSizePixels: _threatSizePixels,
  pickable = false,
  onHover,
  onClick
}) {
  const layers = [];
  const byKind = { threat: threatData, defensive: defensiveData, auv: auvData, friendly: friendlyData };
  const bowByKind = VESSEL_ICON_BOW_COMPASS_AT_ANGLE_ZERO;
  for (const kind of ['threat', 'defensive', 'auv', 'friendly']) {
    const data = byKind[kind];
    if (!data.length) continue;
    const spec = SPECS[kind];
    const iconAtlas = spec.atlas;
    const layerSizePx = iconLayerSizePixelsUniform(sizePixels, spec);
    layers.push(
      new IconLayer({
        id: `${idPrefix}-${kind}`,
        data,
        pickable,
        iconAtlas,
        iconMapping: iconMappingFor(spec),
        getIcon: () => 'icon',
        sizeUnits: 'pixels',
        sizeBasis: 'height',
        sizeScale: 1,
        getSize: layerSizePx,
        getPosition,
        getAngle: (d) => {
          const h = resolveVesselMaritimeHeadingDeg(getHeadingRow(d));
          if (h == null) return 0;
          return deckIconAngleFromMaritimeHeadingDeg(h, bowByKind[kind]);
        },
        getColor: [255, 255, 255, 255],
        billboard: false,
        onHover,
        onClick
      })
    );
  }
  return layers;
}
